import * as layout from '../../../components/markLayout.module.css';
import { MarkButton } from '../../../components/MarkControls';
import React, { useId, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link, Outlet } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import BackButton from '../../../../../components/BackButton';
import InlineLoader from '../../../../../components/InlineLoader';
import QueryInlineError from '../../../../../components/QueryInlineError';
import useBlastDetail from '../../../api/useBlastDetail';
import useValidateBlast from '../../../api/useValidateBlast';
import useSegment from '../../../api/useSegment';
import useBack from '../../../../../hooks/useBack';
import useBlastActions from '../../../hooks/useBlastActions';
import BlastStatusPill from '../BlastStatusPill';
import ActionsBar from './ActionsBar';
import ValidateBanner from './ValidateBanner';
import Overview from './Overview';
import Content from './Content';
import Stats from './Stats';
import PublishModal from './PublishModal';
import ScheduleModal from './ScheduleModal';
import CancelModal from './CancelModal';
import ResumeModal from './ResumeModal';
import SegmentAudiencePreview from '../../../components/SegmentAudiencePreview';
import type { BlastStatus } from '../../../types';
type DetailTab = 'overview' | 'content' | 'stats';
const SHOW_STATS: BlastStatus[] = ['SENDING', 'CANCELLING', 'COMPLETED', 'CANCELLED', 'FAILED'];
const shouldPoll = (status: BlastStatus | undefined): number | false => {
    if (status === 'SENDING' || status === 'CANCELLING') return 15000;
    if (status === 'SCHEDULED') return 60000;
    return false;
};
const BlastDetail: React.FC = () => {
    const { blastId } = useParams<{
        blastId: string;
    }>();
    const navigate = useNavigate();
    const back = useBack();
    const tabId = useId();
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<DetailTab>(() =>
        searchParams.get('tab') === 'content' ? 'content' : 'overview'
    );
    const [contentMounted, setContentMounted] = useState(
        () => searchParams.get('tab') === 'content'
    );
    const [modal, setModal] = useState<'publish' | 'schedule' | 'cancel' | 'resume' | null>(null);
    const numericId = Number(blastId);
    const { data: blastResponse, isLoading, error } = useBlastDetail(numericId, shouldPoll);
    const blast = blastResponse?.data;
    const isDraft = blast?.status === 'DRAFT';
    const validateQuery = useValidateBlast(isDraft ? numericId : 0);
    const isValidated = isDraft
        ? validateQuery.data?.data?.valid === true &&
          !validateQuery.isFetching &&
          !validateQuery.isError
        : true;
    const segmentQuery = useSegment(blast?.segment_id ?? 0);
    const audienceSize = blast?.recipient_count ?? segmentQuery.data?.data?.match_count ?? null;
    const audienceSizeLoading = blast?.recipient_count == null && segmentQuery.isLoading;
    const audienceSizeError = blast?.recipient_count == null && segmentQuery.isError;
    const hasStats = blast && SHOW_STATS.includes(blast.status);
    const tabs: {
        key: DetailTab;
        label: string;
    }[] = [
        { key: 'overview', label: 'Overview' },
        { key: 'content', label: 'Content' },
        ...(hasStats ? [{ key: 'stats' as const, label: 'Stats' }] : [])
    ];
    const selectTab = (tab: DetailTab) => {
        if (tab === 'content') setContentMounted(true);
        setActiveTab(tab);
    };
    const activateTab = (index: number) => {
        const tab = tabs[index];
        selectTab(tab.key);
        requestAnimationFrame(() => document.getElementById(`${tabId}-tab-${tab.key}`)?.focus());
    };
    const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        let nextIndex: number | null = null;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;
        if (nextIndex === null) return;
        event.preventDefault();
        activateTab(nextIndex);
    };
    const closeModal = () => setModal(null);
    // Polling can move the blast out of the status the open modal acts on; drop the modal then.
    const actions = useBlastActions(blast?.status);
    const modalAllowed =
        (modal === 'publish' && actions.canPublish) ||
        (modal === 'schedule' && actions.canSchedule) ||
        (modal === 'cancel' && actions.canCancel) ||
        (modal === 'resume' && actions.canResume);
    if (modal && blast && !modalAllowed) setModal(null);
    if (isLoading) return <InlineLoader />;
    if (error && !blast) return <QueryInlineError error={error} />;
    if (!blast) return <P>Blast not found.</P>;
    return (
        <Section subtheme={['regular', 'background']} className={layout.page} noDefaultPadding>
            <BackButton onBack={() => back('/mark/blasts')} alt="Back to blasts" />
            <P
                noDefaultMargin
                size="small"
                styles={{
                    margin: '0.8rem 0',
                    color: 'var(--mark-muted)'
                }}
            >
                <Link
                    to="/mark"
                    style={{
                        color: 'inherit'
                    }}
                >
                    Mark
                </Link>
                {' > '}
                <Link
                    to="/mark/blasts"
                    style={{
                        color: 'inherit'
                    }}
                >
                    Blasts
                </Link>
                {' > '}
                {blast.name}
            </P>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '1.6rem',
                    marginBottom: '1.2rem'
                }}
            >
                <div>
                    <H role="h4" noDefaultMargin>
                        {blast.name}
                    </H>
                    <div
                        style={{
                            display: 'flex',
                            gap: '0.8rem',
                            alignItems: 'center',
                            marginTop: '0.4rem'
                        }}
                    >
                        <BlastStatusPill status={blast.status} />
                        <P
                            noDefaultMargin
                            size="small"
                            styles={{
                                color: 'var(--mark-muted)'
                            }}
                        >
                            Created: {new Date(blast.created).toLocaleDateString()}
                        </P>
                    </div>
                </div>
                <div className={layout.actions}>
                    <SegmentAudiencePreview
                        key={`${blast.blast_id}-${blast.segment_id}`}
                        segmentId={blast.segment_id}
                        segmentName={blast.segment_name}
                        targetKind={segmentQuery.data?.data?.target_kind ?? ''}
                        description={
                            blast.recipient_count != null
                                ? 'These are current segment matches, which can differ from the recipients already selected for this blast.'
                                : blast.schedule_mode === 'scheduled'
                                  ? 'These are current segment matches. Final recipients are selected when sending starts, after delivery rules and suppression are applied.'
                                  : 'These are current segment matches. Final recipients are selected when you publish, after delivery rules and suppression are applied.'
                        }
                    />
                    <ActionsBar
                        status={blast.status}
                        disableLifecycle={!isValidated}
                        onEdit={() => navigate(`/mark/blasts/${blast.blast_id}/edit`)}
                        onPublish={() => setModal('publish')}
                        onSchedule={() => setModal('schedule')}
                        onCancel={() => setModal('cancel')}
                        onResume={() => setModal('resume')}
                    />
                </div>
            </div>
            {isDraft && (
                <ValidateBanner
                    blastId={blast.blast_id}
                    isContentActive={activeTab === 'content'}
                    onReviewContent={() =>
                        activateTab(tabs.findIndex((tab) => tab.key === 'content'))
                    }
                />
            )}
            <div
                role="tablist"
                aria-label="Blast details"
                style={{
                    borderBottom: '2px solid var(--mark-border)',
                    marginBottom: '1.6rem'
                }}
                className={layout.tabs}
            >
                {tabs.map((tab, index) => (
                    <MarkButton
                        key={tab.key}
                        type="button"
                        role="tab"
                        id={`${tabId}-tab-${tab.key}`}
                        aria-selected={activeTab === tab.key}
                        aria-controls={`${tabId}-panel-${tab.key}`}
                        tabIndex={activeTab === tab.key ? 0 : -1}
                        onClick={() => selectTab(tab.key)}
                        onKeyDown={(event) => handleTabKeyDown(event, index)}
                        style={{
                            marginBottom: '-2px'
                        }}
                    >
                        {tab.label}
                    </MarkButton>
                ))}
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-overview`}
                aria-labelledby={`${tabId}-tab-overview`}
                hidden={activeTab !== 'overview'}
                tabIndex={0}
            >
                <Overview blast={blast} />
            </div>
            <div
                role="tabpanel"
                id={`${tabId}-panel-content`}
                aria-labelledby={`${tabId}-tab-content`}
                hidden={activeTab !== 'content'}
                tabIndex={0}
            >
                {contentMounted && <Content key={blast.blast_id} blast={blast} />}
            </div>
            {hasStats && (
                <div
                    role="tabpanel"
                    id={`${tabId}-panel-stats`}
                    aria-labelledby={`${tabId}-tab-stats`}
                    hidden={activeTab !== 'stats'}
                    tabIndex={0}
                >
                    <Stats blastId={blast.blast_id} status={blast.status} />
                </div>
            )}
            {modal === 'publish' && (
                <PublishModal
                    blast={blast}
                    audienceSize={audienceSize}
                    audienceSizeLoading={audienceSizeLoading}
                    audienceSizeError={audienceSizeError}
                    onRetryAudienceSize={() => segmentQuery.refetch()}
                    onClose={closeModal}
                    onSuccess={closeModal}
                    onSchedule={() => setModal('schedule')}
                />
            )}
            {modal === 'schedule' && (
                <ScheduleModal blast={blast} onClose={closeModal} onSuccess={closeModal} />
            )}
            {modal === 'cancel' && (
                <CancelModal blast={blast} onClose={closeModal} onSuccess={closeModal} />
            )}
            {modal === 'resume' && (
                <ResumeModal blast={blast} onClose={closeModal} onSuccess={closeModal} />
            )}
            <Outlet />
        </Section>
    );
};
export default BlastDetail;
